import { type FormEvent, useContext } from 'react';

import {
  faDownload,
  faPrint,
  faQrcode,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { exportComponentAsJPEG } from 'react-component-export-image';
import { useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import { ExternalPatientContext } from '@/app/external/constants';
import { VideaButton } from '@/common-components/elements/VideaButton/VideaButton';
import {
  previewModeAtom,
  qrCodeErrorAtom,
  qrCodeLoadingAtom,
  showQRCodeAtom,
} from '@/modules/annotator/multiFileViewer/imageToolbar/atoms';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { useGenerateQRCode } from '../hooks/useGenerateQRCode';
import { usePatientPrintoutData } from '../hooks/usePatientPrintoutData';

import { ThemeModeContext } from './QRPrintDownloadContent';

interface PreviewActionButtonsProps {
  componentRef: React.RefObject<HTMLDivElement>;
  patientName?: string;
  setIsPreviewOpen?: (open: boolean) => void;
}

export const PreviewSidebarActionButtons = ({
  componentRef,
  patientName,
  setIsPreviewOpen,
}: PreviewActionButtonsProps) => {
  const mode = useContext(ThemeModeContext);
  const previewMode = useAtomValue(previewModeAtom);
  const showQRCode = useAtomValue(showQRCodeAtom);
  const qrCodeError = useAtomValue(qrCodeErrorAtom);
  const isLoading = useAtomValue(qrCodeLoadingAtom);
  const [, setShowQRCode] = useAtom(showQRCodeAtom);
  const { generateQRCode } = useGenerateQRCode();
  const { isIframe, externalPatientId, externalImageId, patientId } =
    useContext(ExternalPatientContext);
  const { id: selectedPatientId } = useParams<{ id: string }>();
  const { trackEvent } = useTrackEvent();
  const { mixPanelData } = usePatientPrintoutData();

  const handleDownload = (e: FormEvent<HTMLButtonElement>) => {
    trackEvent(mixpanelEvents.previewDialogDownload, {
      ...mixPanelData,
      isIframe: !!isIframe,
      externalPatientId,
      externalImageId,
      patientId: selectedPatientId ?? patientId,
    });
    e.preventDefault();
    const today = new Date().toLocaleDateString();

    // Find the element to export (the one with the print-container class)
    const el = componentRef.current?.querySelector(
      '.print-container',
    ) as HTMLElement | null;
    if (!el) return;

    // Set the scale to 1 (fit) and keep it that way
    el.style.transform = 'scale(1)';

    exportComponentAsJPEG(componentRef, {
      fileName: patientName
        ? patientName.split(' ').concat(today).join('_')
        : `Case_Presentation_Download_${today}`,
    });
  };

  const pageStyle = `
    @page {
      size: letter;
    }
    @media print {
      .print-container {
        transform-origin: top left !important;
        width: 60em !important;
      }
    }
  `;

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    pageStyle,
  });

  const handleGenerateQRCode = async () => {
    await generateQRCode();
  };

  // Common container styling
  const containerClassName =
    mode === 'dark'
      ? 'vai-flex vai-gap-4 vai-p-6 vai-bg-background-dark vai-border-t vai-border-t-sidebar-border'
      : 'vai-flex vai-gap-4 vai-p-6 vai-border-t vai-border-t-zinc-200';

  // QR Code mode logic flow
  if (previewMode === 'qrCode') {
    // Loading state
    if (isLoading) {
      return (
        <div className={containerClassName}>
          <VideaButton
            variant="outlined"
            className="vai-bg-sapphire-600 hover:vai-bg-sapphire-700 vai-w-full"
            disabled={true}
          >
            <span className="vai-text-white vai-text-sm vai-font-medium">
              <FontAwesomeIcon
                icon={faQrcode}
                className="vai-mr-2 vai-font-inter"
              />
              <Trans>Generating...</Trans>
            </span>
          </VideaButton>
        </div>
      );
    }

    // Error state
    if (qrCodeError) {
      return (
        <div className={containerClassName}>
          <VideaButton
            variant="outlined"
            className={classNames('vai-w-full', {
              'vai-text-white vai-bg-background-muted hover:vai-bg-background-gray':
                mode === 'dark',
            })}
            onClick={handleGenerateQRCode}
          >
            <span className="vai-text-white vai-text-sm vai-font-medium">
              <Trans>Try Again</Trans>
            </span>
          </VideaButton>
        </div>
      );
    }

    // Data state (QR code generated successfully)
    if (showQRCode) {
      return (
        <div className={containerClassName}>
          <VideaButton
            variant="outlined"
            className="vai-text-white vai-bg-background-muted hover:vai-bg-background-gray vai-w-full"
            onClick={() => setShowQRCode(false)}
          >
            <span className="vai-text-white vai-text-sm vai-font-medium">
              <Trans>Edit More</Trans>
            </span>
          </VideaButton>
          {!isIframe && (
            <VideaButton
              variant="contained"
              className="vai-bg-sapphire-600 hover:vai-bg-sapphire-700 vai-w-full"
              onClick={() => setIsPreviewOpen && setIsPreviewOpen(false)}
            >
              <span className="vai-text-white vai-text-sm vai-font-medium">
                <Trans>Done</Trans>
              </span>
            </VideaButton>
          )}
        </div>
      );
    }

    // Initial QR code state (not generated yet)
    return (
      <div className={containerClassName}>
        <VideaButton
          variant="outlined"
          className="vai-bg-sapphire-600 hover:vai-bg-sapphire-700 vai-w-full"
          onClick={handleGenerateQRCode}
        >
          <span className="vai-text-white vai-text-sm vai-font-medium">
            <FontAwesomeIcon
              icon={faQrcode}
              className="vai-mr-2 vai-font-inter"
            />
            <Trans>Generate QR Code</Trans>
          </span>
        </VideaButton>
      </div>
    );
  }

  // Print mode
  if (previewMode === 'print') {
    return (
      <div className={containerClassName}>
        <VideaButton
          variant="outlined"
          className="vai-text-white vai-bg-background-muted hover:vai-bg-background-gray vai-w-full"
          onClick={handleDownload}
        >
          <span className="vai-text-white vai-text-sm vai-font-medium">
            <FontAwesomeIcon
              icon={faDownload}
              className="vai-mr-2 vai-font-inter"
            />
            <Trans>Download</Trans>
          </span>
        </VideaButton>
        <VideaButton
          variant="contained"
          className="vai-bg-sapphire-600 hover:vai-bg-sapphire-700 vai-w-full"
          onClick={() => {
            trackEvent(mixpanelEvents.previewDialogPrint, {
              ...mixPanelData,
              isIframe: !!isIframe,
              externalPatientId,
              externalImageId,
              patientId: selectedPatientId ?? patientId,
            });
            handlePrint();
          }}
        >
          <span className="vai-text-white vai-text-sm vai-font-medium">
            <FontAwesomeIcon
              icon={faPrint}
              className="vai-mr-2 vai-font-inter"
            />
            <Trans>Print</Trans>
          </span>
        </VideaButton>
      </div>
    );
  }

  // Default state (no mode selected)
  return (
    <div className={containerClassName}>
      {/* Empty state or default content */}
    </div>
  );
};
